/**
 * Loading the puzzles a printed book actually contains.
 *
 * The site must never regenerate a puzzle from its seed. Seeds reproduce a book
 * only against the generator that made it, and the generator moves — raising
 * the Easy band from technique level 1 to 2 silently changed what every Easy
 * seed produces. A reader holding a book printed last year needs the puzzle
 * that is on their page, so the only safe source is a snapshot taken at
 * publication and never touched again.
 *
 * Those snapshots live in site/public/books/<slug>/, written by
 * site/scripts/archive-book.ts. Being under `public/` is what makes them work
 * with no machinery at all: Vite serves them in dev and copies them into the
 * build, at the same URL in both. They were going to sit at the repo root
 * instead, which would have needed a copy step in the build *and* a dev-server
 * middleware to serve one directory.
 *
 * One file per puzzle rather than one per book: a whole book is ~430KB of JSON,
 * a single puzzle ~2KB minified, and the reader arriving by QR code is on a
 * phone and wants exactly one of them.
 */
import type { Puzzle } from "@gen/logic-grid/types.ts";

/** Where a single archived puzzle lives, relative to the site root. */
export function puzzleUrl(slug: string, number: number): string {
  return `/books/${slug}/${number}.json`;
}

export class PuzzleNotFound extends Error {
  constructor(slug: string, number: number) {
    super(`No puzzle ${number} archived for "${slug}"`);
    this.name = "PuzzleNotFound";
  }
}

/**
 * One archived puzzle. Puzzle numbers are 1-based, matching how they are
 * printed — a reader types what is on the page, not an array index.
 *
 * `res.ok` is not the whole test, because a miss does not have to arrive as a
 * 404. The deploy prerenders one file per route, so an unmatched path does get
 * a real 404 from Cloudflare Pages today — but any host or preview layer that
 * falls back to the app shell answers 200 with index.html instead, and without
 * the check below that HTML would reach `res.json()` and surface as a
 * SyntaxError from a caller that was watching for PuzzleNotFound. The same
 * guard covers a deploy that shipped the routes but not the archive.
 *
 * A malformed archive file is deliberately *not* folded in here: that is a
 * broken deploy rather than a puzzle nobody archived, and it should not be able
 * to hide behind the same error.
 */
export async function loadPuzzle(slug: string, number: number): Promise<Puzzle> {
  const res = await fetch(puzzleUrl(slug, number));
  const type = res.headers.get("content-type") ?? "";
  if (!res.ok || !type.includes("json")) throw new PuzzleNotFound(slug, number);
  return (await res.json()) as Puzzle;
}
